import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'What exactly is a Basetao Spreadsheet?',
    answer: 'A Basetao Spreadsheet is a structured product database that organizes product links, categories, prices, QC photos, sizing notes, and shipping information all in one accessible format. It saves shoppers hours of manual searching by consolidating curated product discoveries into a searchable, filterable format. Think of it as your personal shopping assistant that keeps track of everything worth buying.',
  },
  {
    question: 'Is Basetao a legitimate and safe platform to use?',
    answer: 'Yes, Basetao is a well-established shopping agent platform that has been operating for years with thousands of satisfied users worldwide. The platform provides buyer protection, quality inspection services, and transparent shipping options. However, as with any international shopping platform, users should review product listings carefully, check seller ratings, and use the QC photo service before shipping items internationally.',
  },
  {
    question: 'How much does Basetao shipping typically cost?',
    answer: 'Shipping costs vary based on package weight, dimensions, destination country, and chosen shipping method. Economy shipping lines typically range from $15-30 for small packages under 1kg, while express carriers like DHL or FedEx can range from $30-80+ depending on weight. The Basetao platform provides real-time shipping calculators so you can estimate costs before finalizing your order. Using consolidation and choosing slower shipping methods can significantly reduce costs.',
  },
  {
    question: 'How do I find the best products using the spreadsheet?',
    answer: 'The spreadsheet organizes products by category, price range, popularity, and QC status. Start by browsing your preferred category, then filter by your budget range. Look for items with completed QC photos and positive notes. Pay attention to sizing charts and material notes included in the spreadsheet. Experienced users recommend cross-referencing multiple listings before making a final decision, and always checking the most recently updated entries for the freshest finds.',
  },
  {
    question: 'Are there any available Basetao coupons or discount codes?',
    answer: 'Yes, Basetao periodically offers coupons and discount codes, especially for new users and during seasonal promotions. Common discounts include sign-up bonuses, shipping fee reductions, and service fee waivers. The best strategy is to check for active codes before placing each order, as codes are regularly updated. Combining multiple orders and using consolidation can also effectively reduce your total costs beyond what coupons alone can achieve.',
  },
  {
    question: 'How often is the Basetao Spreadsheet updated?',
    answer: 'The Basetao Spreadsheet is continuously updated by the community and platform contributors. New product finds are added daily, QC information is refreshed as orders are processed, and pricing data receives regular updates. The 2026 version includes enhanced categorization, improved searchability, and more detailed product notes compared to previous editions. We recommend checking back frequently for the latest additions and verified product listings.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="w-full py-16 md:py-20 bg-background-100">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-secondary-200 text-secondary-700 text-xs font-semibold uppercase tracking-wide mb-4">
            FAQ
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-base text-foreground-600">
            Quick answers to the most common questions about Basetao Spreadsheet.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-background-50/70 transition-colors"
              >
                <span className="font-heading text-base font-semibold text-foreground-900 pr-4">
                  {faq.question}
                </span>
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-lg text-foreground-500"></i>
                </div>
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-5 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/basetao-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-accent-100 text-accent-700 text-sm font-medium hover:bg-accent-200 transition-colors whitespace-nowrap"
          >
            <span>Read Full Guide</span>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}